/**
 * 전장의 낱소리(알갱이)와 깔개를 코드로 만든다. 알갱이는 아주 짧고 작다 — 한꺼번에 여럿이 난다(`battle-mix.ts`가 묶는다).
 * 신호 소리(`synth.ts`)와 같은 까닭으로 파일이 없다.
 *
 * **만든 쪽은 듣지 못한다. 고른 것은 플레이한 사람이다**(2026-10-08) — 종류마다 후보 셋에서 다섯을, 실제 판에서 뽑은
 * 타격의 흐름(`World.fx`를 틱마다 적은 것)으로 틀어 보는 쪽에서 들어 보고 골랐다:
 * 칼은 챙·쨍·퍽을 섞고, 창은 푹·탁·텅을 섞고, 화살을 쏠 때는 바람만(시위 소리 없이), 맞을 때는 툭·탁을 섞고,
 * 쓰러질 때는 조용히 풀썩(쿵이 아니다), 깔개는 낮은 울림("웅웅" — 타격 소리를 가리지 않는다). 잦기는 "적게"다.
 *
 * 알갱이는 `Math.random`을 쓴다(잡음의 시작 자리, 섞음의 고르기) — 같은 알갱이가 겹쳐도 똑같은 파형이 포개지지 않게.
 * 시뮬레이션 밖이라 판의 결과와 무관하다.
 */
import type { GrainKind } from './battle-mix.ts';

type Ctx = BaseAudioContext;
/** g = 크기(0~1), p = 음높이 배율. */
export type Grain = (ctx: Ctx, out: AudioNode, t: number, g: number, p: number) => void;

const noiseOf = new WeakMap<Ctx, AudioBuffer>();

function noise(ctx: Ctx): AudioBuffer {
  let buf = noiseOf.get(ctx);
  if (buf) return buf;
  buf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let s = 0x51f15e;
  for (let i = 0; i < d.length; i++) {
    s = (Math.imul(s, 1664525) + 1013904223) | 0;
    d[i] = (s >>> 8) / 0x800000 - 1;
  }
  noiseOf.set(ctx, buf);
  return buf;
}

function shape(g: GainNode, t: number, peak: number, attack: number, decay: number): void {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(Math.max(0.0001, peak), t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
}

function nz(ctx: Ctx, out: AudioNode, t: number, type: BiquadFilterType, freq: number, q: number, peak: number, attack: number, decay: number, to = freq): void {
  const src = ctx.createBufferSource();
  src.buffer = noise(ctx);
  const f = ctx.createBiquadFilter();
  f.type = type;
  f.Q.value = q;
  f.frequency.setValueAtTime(freq, t);
  if (to !== freq) f.frequency.exponentialRampToValueAtTime(to, t + attack + decay);
  const g = ctx.createGain();
  shape(g, t, peak, attack, decay);
  src.connect(f).connect(g).connect(out);
  // 잡음 버퍼의 아무 데서나 시작한다(머리말).
  src.start(t, Math.random() * 0.8);
  src.stop(t + attack + decay + 0.03);
}

function tone(ctx: Ctx, out: AudioNode, t: number, type: OscillatorType, f: number, to: number, peak: number, decay: number): void {
  const o = ctx.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(f, t);
  if (to !== f) o.frequency.exponentialRampToValueAtTime(to, t + decay);
  const g = ctx.createGain();
  shape(g, t, peak, 0.002, decay);
  o.connect(g).connect(out);
  o.start(t);
  o.stop(t + decay + 0.03);
}

/** 쇠가 우는 소리: 어긋난 높은 사인파 셋. */
function clang(ctx: Ctx, out: AudioNode, t: number, f: number, peak: number, decay: number): void {
  tone(ctx, out, t, 'sine', f, f, peak, decay);
  tone(ctx, out, t, 'sine', f * 1.47, f * 1.47, peak * 0.6, decay * 0.8);
  tone(ctx, out, t, 'sine', f * 2.63, f * 2.63, peak * 0.35, decay * 0.5);
}

// 칼: 챙(쇠가 부딪친다)·쨍(쇠가 운다)·퍽(둔하게 맞는다).
const cutClash: Grain = (c, o, t, g, p) => {
  nz(c, o, t, 'bandpass', 1900 * p, 3, 0.5 * g, 0.001, 0.045);
  tone(c, o, t, 'triangle', 950 * p, 800 * p, 0.12 * g, 0.04);
};
const cutRing: Grain = (c, o, t, g, p) => {
  clang(c, o, t, 2100 * p, 0.22 * g, 0.13);
  nz(c, o, t, 'highpass', 3500, 0.7, 0.14 * g, 0.001, 0.02);
};
const cutFlesh: Grain = (c, o, t, g, p) => {
  nz(c, o, t, 'lowpass', 700 * p, 0.8, 0.5 * g, 0.002, 0.06);
  tone(c, o, t, 'sine', 190 * p, 110 * p, 0.3 * g, 0.07);
};

// 창: 푹(낮게 박힌다)·탁(자루가 부딪친다)·텅(방패에 막힌다).
const thThump: Grain = (c, o, t, g, p) => {
  nz(c, o, t, 'lowpass', 450 * p, 0.8, 0.55 * g, 0.002, 0.07);
  tone(c, o, t, 'sine', 230 * p, 105 * p, 0.32 * g, 0.06);
};
const thWood: Grain = (c, o, t, g, p) => {
  tone(c, o, t, 'sine', 520 * p, 430 * p, 0.3 * g, 0.035);
  nz(c, o, t, 'bandpass', 1200 * p, 2.5, 0.3 * g, 0.001, 0.025);
};
const thShield: Grain = (c, o, t, g, p) => {
  nz(c, o, t, 'bandpass', 800 * p, 2, 0.5 * g, 0.001, 0.05);
  tone(c, o, t, 'triangle', 300 * p, 240 * p, 0.2 * g, 0.05);
};

// 화살: 쏠 때는 바람만(쉭) — 시위가 튕기는 소리는 궁수가 많으면 튄다 / 박힌다(툭)·방패나 갑옷에 맞는다(탁).
const outWhoosh: Grain = (c, o, t, g, p) => {
  nz(c, o, t, 'bandpass', 900 * p, 1.4, 0.3 * g, 0.04, 0.1, 3400 * p);
};
const hitThunk: Grain = (c, o, t, g, p) => {
  nz(c, o, t, 'lowpass', 950 * p, 0.8, 0.45 * g, 0.001, 0.035);
  tone(c, o, t, 'sine', 300 * p, 200 * p, 0.2 * g, 0.035);
};
const hitTick: Grain = (c, o, t, g, p) => {
  nz(c, o, t, 'bandpass', 2600 * p, 4, 0.45 * g, 0.001, 0.02);
  tone(c, o, t, 'sine', 700 * p, 600 * p, 0.14 * g, 0.03);
};

// 쓰러진다: 풀썩. 음 없이 둔한 바람 소리만 — 쓰러질 때마다 쿵 하면 판이 무거워진다.
const fallSoft: Grain = (c, o, t, g, p) => {
  nz(c, o, t, 'lowpass', 380 * p, 0.8, 0.4 * g, 0.01, 0.18);
};

// 말발굽: 흙을 딛는 둔한 소리(고른 것) / 단단한 땅의 또각 / 음 없이 부드럽게.
const hoofEarth: Grain = (c, o, t, g, p) => {
  nz(c, o, t, 'lowpass', 320 * p, 0.9, 0.5 * g, 0.002, 0.045);
  tone(c, o, t, 'sine', 120 * p, 68 * p, 0.34 * g, 0.05);
};
const hoofClop: Grain = (c, o, t, g, p) => {
  nz(c, o, t, 'bandpass', 900 * p, 3, 0.42 * g, 0.001, 0.028);
  tone(c, o, t, 'sine', 190 * p, 150 * p, 0.22 * g, 0.03);
};
const hoofSoft: Grain = (c, o, t, g, p) => {
  nz(c, o, t, 'lowpass', 210 * p, 0.8, 0.6 * g, 0.004, 0.07);
};
/** 말발굽의 후보(들어 보는 쪽이 쓴다). 고른 것이 `GRAINS.hoof`다. */
export const HOOF_GRAINS = { earth: hoofEarth, clop: hoofClop, soft: hoofSoft } as const;

const pick = (r: number, a: Grain, pa: number, b: Grain, pb: number, c: Grain): Grain => (r < pa ? a : r < pa + pb ? b : c);

/** 종류마다의 알갱이. 섞는 것은 같은 소리의 되풀이를 줄이려는 것이다. */
export const GRAINS: Readonly<Record<GrainKind, Grain>> = {
  cut: (c, o, t, g, p) => pick(Math.random(), cutClash, 0.6, cutRing, 0.2, cutFlesh)(c, o, t, g, p),
  thrust: (c, o, t, g, p) => pick(Math.random(), thThump, 0.5, thWood, 0.3, thShield)(c, o, t, g, p),
  arrowOut: outWhoosh,
  arrowHit: (c, o, t, g, p) => (Math.random() < 0.6 ? hitThunk : hitTick)(c, o, t, g, p),
  fall: fallSoft,
  hoof: hoofEarth,
};

/** 알갱이 하나가 들리는 가장 긴 길이(ms) — 좌우 마디를 떼는 때를 정한다. */
export const GRAIN_MAX_MS = 400;

/** 전장의 깔개. `level(0~1)`로 크기를 맞춘다. */
export interface Bed {
  level(v: number): void;
  stop(): void;
}

/**
 * 깔개 "웅웅": 낮은 울림(240Hz 아래)에 420Hz의 띠를 얇게 얹고, 띠가 천천히 오르내린다 — 땅이 울리는 소리.
 * 사람 목소리 높이의 "술렁임"도 후보였으나 낮은 쪽이 뽑혔다(타격 소리를 가리지 않는다).
 * 3초짜리 잡음을 돌린다 — 짧으면 되풀이가 귀에 걸린다.
 */
export function makeBed(ctx: AudioContext, out: AudioNode): Bed {
  const len = ctx.sampleRate * 3;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let brown = 0;
  for (let i = 0; i < len; i++) {
    const white = Math.random() * 2 - 1;
    brown = (brown + 0.02 * white) / 1.02;
    d[i] = white * 0.5 + brown * 3.0;
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  const master = ctx.createGain();
  master.gain.value = 0;
  master.connect(out);
  const lfos: OscillatorNode[] = [];
  const bands: readonly (readonly [BiquadFilterType, number, number, number, number])[] = [
    ['lowpass', 240, 0.8, 1.0, 0.2],
    ['bandpass', 420, 1.2, 0.3, 0.3],
  ];
  for (const [type, freq, q, w, wobble] of bands) {
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    f.Q.value = q;
    const g = ctx.createGain();
    g.gain.value = w;
    src.connect(f).connect(g).connect(master);
    if (wobble > 0) {
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.13 + Math.random() * 0.4;
      const depth = ctx.createGain();
      depth.gain.value = freq * wobble;
      lfo.connect(depth).connect(f.frequency);
      lfo.start();
      lfos.push(lfo);
    }
  }
  src.start();
  const PEAK = 0.26;
  return {
    level: (v) => master.gain.setTargetAtTime(Math.max(0, Math.min(2, v)) * PEAK, ctx.currentTime, 0.25),
    stop: () => {
      try {
        master.gain.cancelScheduledValues(ctx.currentTime);
        master.gain.setTargetAtTime(0, ctx.currentTime, 0.04);
        setTimeout(() => {
          try {
            src.stop();
            for (const l of lfos) l.stop();
            master.disconnect();
          } catch {
            // 이미 멈췄다.
          }
        }, 300);
      } catch {
        // 못 멈춰도 줄이 끊기면 조용해진다.
      }
    },
  };
}
